import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { errorText } from '../../pages/SoftwarePolicy/policyEdit';
import { useV2Account } from '../useV2Account';

import ApplicationView from './ApplicationView';
import { inLineApplications } from './applicationDrift';
import { gap, hasJob, list, matchedText, MODE, NEEDS_SCRIPT, scopeText, shownName, versionText } from './driftShared';
import { ActionChoice, InfoLink } from './DriftControls';
import { canRemove, cannotRemoveBecause, queueRemovals } from './driftRemove';
import { SortHeader, useSort } from './sortable';

export { hasJob, versionText };
export const fleetUrl = (account) => `cma/softwaredriftfleet?${new URLSearchParams({ account }).toString()}`;
export const QUEUE_URL = 'cma/softwaredrift/queue';
export const QUEUE_APPLICATION_URL = 'cma/softwaredrift/queueapplication';

export const findingRef = (f) => ({ key: f.key, name: f.name, gap: f.gap });
const selectionId = (f) => `${f.key}|${f.name}|${f.gap}`;
const sorted = (findings) => [...list(findings)].sort((a, b) => gap(a.gap).rank - gap(b.gap).rank || shownName(a).localeCompare(shownName(b)));
const counts = (machine) => {
  const out = {};
  list(machine.findings).forEach((f) => { out[f.gap] = (out[f.gap] || 0) + 1; });
  return Object.entries(out).sort((a, b) => gap(a[0]).rank - gap(b[0]).rank);
};

function MachineFindings({ machine, onQueue, onRemove }) {
  const findings = sorted(machine.findings);
  const name = machine.computer || machine.deviceId;
  const [selected, setSelected] = useState({});
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const withJobs = findings.filter(hasJob);
  const removable = findings.filter(canRemove);
  // Update or Remove: update unless no script can update anything here and something can be removed.
  const [action, setAction] = useState(withJobs.length === 0 && removable.length > 0 ? 'remove' : 'update');
  const removing = action === 'remove';
  const choosable = removing ? removable : withJobs;
  const chosen = choosable.filter((f) => selected[selectionId(f)]);
  const allChosen = choosable.length > 0 && chosen.length === choosable.length;
  const choose = (next) => { setAction(next); setSelected({}); setOutcome(null); };

  const queue = async (kind) => {
    setBusy(true);
    setOutcome(null);
    try {
      const result = removing ? await onRemove(machine, kind, chosen) : await onQueue(machine, kind, chosen.map(findingRef));
      setOutcome({ kind, ...result });
      setSelected({});
    } catch (ex) {
      setOutcome({ kind, error: errorText(ex) });
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  return (
    <div className="v2-detail">
      <div className="v2-detail-head"><h3>{`Findings of ${name}`}</h3></div>
      {outcome?.error && <div className="v2-alert err" role="alert">{outcome.error}</div>}
      {outcome && !outcome.error && (
        <div className={`v2-alert${list(outcome.refused).length || list(outcome.failed).length ? ' info' : ''}`} role="status">
          {`${list(outcome.queued).length} ${outcome.kind === 'check' ? 'test' : 'apply'} ${list(outcome.queued).length === 1 ? 'job' : 'jobs'} in the queue of ${name}.`}
          {list(outcome.queued).map((q) => <div className="v2-sub" key={q.id}>{`${q.program || q.name}: ${q.id}`}</div>)}
          {list(outcome.refused).map((r) => <div className="v2-sub" key={`r${r.program || r.name}`}>{`Not queued, ${r.program || r.name}: ${r.error}`}</div>)}
          {list(outcome.failed).map((r) => <div className="v2-sub" key={`f${r.program || r.name}`}>{`Failed, ${r.program || r.name}: ${r.error}`}</div>)}
        </div>
      )}
      <div className="v2-tools">
        <label className="v2-check">
          <input type="checkbox" checked={allChosen} disabled={choosable.length === 0 || busy}
            onChange={(e) => setSelected(e.target.checked ? Object.fromEntries(choosable.map((f) => [selectionId(f), true])) : {})} />
          {removing ? `Select all that can be removed (${removable.length})` : `Select all with a script (${withJobs.length})`}
        </label>
        {removable.length > 0 && <ActionChoice value={action} onChange={choose} canUpdate={withJobs.length > 0} disabled={busy} />}
        <button type="button" className="v2-btn" disabled={chosen.length === 0 || busy} onClick={() => queue('check')}>{`Test (${chosen.length})`}</button>
        <button type="button" className="v2-btn primary" disabled={chosen.length === 0 || busy} onClick={() => setConfirming(true)}>{`Apply (${chosen.length})`}</button>
        {busy && <span className="v2-spin" role="status" aria-label="Queueing" />}
      </div>
      <div className="v2-items">
        {findings.map((f, i) => (
          <div className="v2-finding" key={`${f.key}-${i}`}>
            <div>
              <input type="checkbox" aria-label={`Select ${shownName(f)}`} disabled={!choosable.includes(f) || busy}
                title={removing ? cannotRemoveBecause(f) : (hasJob(f) ? '' : 'No script can do this yet.')}
                checked={Boolean(selected[selectionId(f)])} onChange={(e) => setSelected({ ...selected, [selectionId(f)]: e.target.checked })} />
            </div>
            <div><span className={`v2-pill ${gap(f.gap).tone}`} title={gap(f.gap).meaning}>{gap(f.gap).label}</span></div>
            <div className="name">{shownName(f)}<InfoLink wingetId={f.wingetId} /></div>
            <div>
              {versionText(f)}
              {matchedText(f) && <div className="v2-sub">{matchedText(f)}</div>}
              {scopeText(f.scope) && <div className="v2-sub">{scopeText(f.scope)}</div>}
            </div>
            <div>{MODE[f.mode] || ''}<div className="v2-sub">{f.decidedBy}</div></div>
            <div>
              {f.note && <div>{f.note}</div>}
              {!removing && !hasJob(f) && NEEDS_SCRIPT.includes(f.gap) && <div className="v2-sub">No script can do this yet.</div>}
            </div>
          </div>
        ))}
      </div>

      {confirming && (
        <div className="v2-dialog-host">
          <div className="v2-dialog" role="dialog" aria-label={`Apply to ${name}`}>
            <h2>{`Apply to ${name}?`}</h2>
            <p>{`${chosen.length} ${chosen.length === 1 ? 'job' : 'jobs'} will be put in the queue of this machine and run for real: ${removing ? 'they remove software' : 'they install, update or remove software'}.`}</p>
            {chosen.map((f) => <div key={selectionId(f)}>{`${gap(f.gap).label}: ${shownName(f)}`}</div>)}
            <div className="actions">
              <button type="button" className="v2-btn" onClick={() => setConfirming(false)} disabled={busy}>Cancel</button>
              <button type="button" className="v2-btn primary" onClick={() => queue('apply')} disabled={busy}>Apply</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Software drift, V2: each machine of an account against its profiles. A machine opens to its findings (the others are hidden); findings
// that have a script can be ticked and put in the queue as a test (dry run) or for real after a confirmation.
export default function DriftPage() {
  const jApi = useApi();
  const apiRef = useRef(jApi);
  apiRef.current = jApi;
  const { accounts, account, choose, error: accountsError } = useV2Account();
  const [report, setReport] = useState({ state: 'idle' });
  const [open, setOpen] = useState(null);
  const [view, setView] = useState('machine');
  const machineSort = useSort();
  const [showAll, setShowAll] = useState(false);
  const [showGoodApps, setShowGoodApps] = useState(false);
  const code = account?.code;

  const load = useCallback(async () => {
    if (!code) return;
    setReport({ state: 'loading' });
    try {
      const response = await apiRef.current.request('GET', fleetUrl(code));
      setReport({ state: 'ok', body: response.data || {} });
    } catch (ex) {
      setReport({ state: 'error', error: errorText(ex) });
    }
  }, [code]);

  useEffect(() => { setOpen(null); load(); }, [load]);

  // The page sends only the names of the findings; the jobs are taken from the API's own drift result.
  const queueJobs = useCallback(async (machine, kind, findings) => {
    const response = await apiRef.current.request('POST', QUEUE_URL, { account: code, deviceId: machine.deviceId, kind, findings });
    return response.data;
  }, [code]);

  // One application on several machines: the page sends the application's key, the kind of finding and the machine ids, never a script.
  const queueApplication = useCallback(async (kind, key, findingGap, deviceIds) => {
    const response = await apiRef.current.request('POST', QUEUE_APPLICATION_URL, { account: code, kind, key, gap: findingGap, deviceIds });
    return response.data;
  }, [code]);

  // A removal of one exact copy (the name and version the machine reported), asked of the Devices page's removal route.
  const queueRemove = useCallback((kind, items) => queueRemovals(apiRef.current.request, code, kind, items), [code]);
  const removeOnMachine = useCallback((machine, kind, findings) => queueRemove(kind, findings.map((f) => ({ deviceIds: [machine.deviceId], name: f.name, version: f.installed }))), [queueRemove]);

  const body = report.state === 'ok' ? report.body : {};
  const machines = list(body.machines);
  const summary = body.summary || {};
  const idOf = (m) => m.deviceId || m.computer;
  // A machine with no findings is in line with its profiles: by default only the machines with something to look at are listed.
  const inLine = (m) => m.status !== 'error' && list(m.findings).length === 0;
  const inLineCount = machines.filter(inLine).length;
  const inLineApps = inLineApplications(machines).length;
  const visible = showAll ? machines : machines.filter((m) => !inLine(m));
  const sortedMachines = machineSort.apply(visible, {
    machine: { get: (m) => m.computer || m.deviceId },
    findings: { get: (m) => list(m.findings).length },
    profiles: { get: (m) => list(m.ruleSets).map((r) => r.filterText || r.description).join(' ') },
    reported: { get: (m) => m.updatedUtc || '' },
  });

  return (
    <div>
      <div className="v2-head">
        <div>
          <h1>Software drift</h1>
          <p>Each machine against its profiles: what is missing, out of date or not allowed, with the job that fixes it.</p>
        </div>
        <div>
          <select className="v2-select v2-account" aria-label="Account" value={code || ''} onChange={(e) => choose(e.target.value)}>
            {accounts.map((a) => <option key={a.code} value={a.code}>{a.name ? `${a.code} - ${a.name}` : a.code}</option>)}
          </select>
        </div>
      </div>

      {accountsError && <div className="v2-alert err">{accountsError}</div>}
      {report.state === 'error' && <div className="v2-alert err">{report.error}</div>}
      {report.state === 'ok' && body.policy?.status === 'none' && <div className="v2-alert info">This account has no software policy yet, so there is nothing to compare with.</div>}
      {report.state === 'ok' && body.dataComplete === false && <div className="v2-alert info">Not every machine result could be read, so some machines may be missing.</div>}

      <section className="v2-card">
        {report.state === 'loading' && <div className="v2-loading"><span className="v2-spin" role="status" aria-label="Loading" /></div>}
        {report.state === 'ok' && (
          <>
            <div className="v2-bar">
              <div role="group" aria-label="View">
                <button type="button" className={`v2-btn${view === 'machine' ? ' primary' : ''}`} aria-pressed={view === 'machine'} onClick={() => setView('machine')}>By machine</button>
                <button type="button" className={`v2-btn${view === 'application' ? ' primary' : ''}`} aria-pressed={view === 'application'} onClick={() => setView('application')}>By application</button>
              </div>
              {view === 'machine' && (
                <label className="v2-check">
                  <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
                  {`Show machines in line with the policy (${inLineCount})`}
                </label>
              )}
              {view === 'application' && (
                <label className="v2-check">
                  <input type="checkbox" checked={showGoodApps} onChange={(e) => setShowGoodApps(e.target.checked)} />
                  {`Show applications in line with the policy (${inLineApps})`}
                </label>
              )}
              <div>
                <span className="v2-pill none">{`${summary.machines || 0} machines`}</span>
                <span className={`v2-pill ${summary.withFindings ? 'warn' : 'ok'}`}>{`${summary.withFindings || 0} with findings`}</span>
                <span className="v2-pill none">{`${summary.findings || 0} findings`}</span>
              </div>
              {list(body.machinesWithoutResult).length > 0 && (
                <span className="v2-sub" title={body.machinesWithoutResult.join(', ')}>{`${body.machinesWithoutResult.length} ${body.machinesWithoutResult.length === 1 ? 'machine has' : 'machines have'} not reported yet`}</span>
              )}
            </div>
            {view === 'application' && machines.length > 0 ? <ApplicationView machines={machines} onQueue={queueApplication} onRemove={queueRemove} showInLine={showGoodApps} />
              : machines.length === 0 ? <div className="v2-empty">No machine of this account has reported its software yet.</div>
                : visible.length === 0 ? <div className="v2-empty">Every machine is in line with its profiles.</div> : (
              <table className="v2-table" aria-label="Software drift by machine">
                <thead>
                  <tr>
                    <th style={{ width: 30 }} />
                    <SortHeader k="machine" sort={machineSort.sort} onSort={machineSort.toggle}>Machine</SortHeader>
                    <SortHeader k="findings" sort={machineSort.sort} onSort={machineSort.toggle}>Findings</SortHeader>
                    <SortHeader k="profiles" sort={machineSort.sort} onSort={machineSort.toggle}>Profiles that apply</SortHeader>
                    <SortHeader k="reported" sort={machineSort.sort} onSort={machineSort.toggle}>Reported</SortHeader>
                  </tr>
                </thead>
                <tbody>
                  {sortedMachines.filter((m) => !open || open === idOf(m)).map((m) => {
                    const isOpen = open === idOf(m);
                    const chips = counts(m);
                    const toggle = () => { if (list(m.findings).length) setOpen(isOpen ? null : idOf(m)); };
                    return (
                      <React.Fragment key={idOf(m)}>
                        <tr className="row" aria-expanded={isOpen} tabIndex={0} onClick={toggle}
                          onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) { e.preventDefault(); toggle(); } }}>
                          <td><span className="v2-caret" aria-hidden="true">&#9656;</span></td>
                          <td style={{ fontWeight: 700 }}>{m.computer || m.deviceId}</td>
                          <td>
                            {m.status === 'error' ? <span className="v2-pill err">{m.message || 'Failed'}</span>
                              : chips.length === 0 ? <span className="v2-pill ok">In line with the policy</span>
                                : chips.map(([g, n]) => <span key={g} className={`v2-pill ${gap(g).tone}`} title={gap(g).meaning}>{`${gap(g).label} ${n}`}</span>)}
                          </td>
                          <td>{list(m.ruleSets).map((r) => <span className="v2-chip" key={r.id || r.filterText}>{r.filterText || r.description}</span>)}</td>
                          <td>{m.updatedUtc ? m.updatedUtc.slice(0, 16).replace('T', ' ') : ''}</td>
                        </tr>
                        {isOpen && <tr className="detail"><td colSpan={5}><MachineFindings machine={m} onQueue={queueJobs} onRemove={removeOnMachine} /></td></tr>}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </>
        )}
      </section>
    </div>
  );
}
